import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, HeartStraight, Coins, TrendUp, CalendarCheck, ArrowRight } from "@phosphor-icons/react";
import { E } from "@/components/Editable";

const benefits = [
  {
    icon: Briefcase, color: "#10B981", tag: "01 / WORK",
    title: "Career Clarity",
    text: "Untangle career crossroads. The cards surface the ambitions and blind spots you already carry — so decisions about jobs, ventures and leadership become conscious, not accidental.",
    stat: "73%", statLabel: "of querents report clearer next steps after a career spread",
  },
  {
    icon: HeartStraight, color: "#EC4899", tag: "02 / RELATIONSHIPS",
    title: "Deeper Connection",
    text: "See your patterns in love and friendship reflected back. Tarot acts as a structured mirror for attachment styles, unspoken needs and the conversations you've been avoiding.",
    stat: "2×", statLabel: "more likely to initiate honest conversations post-reading",
  },
  {
    icon: Coins, color: "#F59E0B", tag: "03 / MONEY",
    title: "Money Mindset",
    text: "Your relationship with money is a story you tell yourself. Spreads expose scarcity loops and abundance blocks, turning vague financial anxiety into a concrete plan.",
    stat: "5 of 10", statLabel: "money blocks are subconscious — spreads make them visible",
  },
  {
    icon: TrendUp, color: "#D4AF37", tag: "04 / PROSPERITY",
    title: "Whole-Life Prosperity",
    text: "Prosperity is alignment, not just accumulation. A full-spectrum reading maps work, love and wealth into one coherent picture of where your energy should flow next.",
    stat: "78", statLabel: "cards, one integrated map of your momentum",
  },
];

export const BenefitsSection = () => (
  <section className="relative py-24 lg:py-36 px-6 lg:px-10 max-w-7xl mx-auto" id="benefits" data-testid="benefits-section">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-4">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-4"><E k="benefits_overline" f="Why the cards work" /></p>
        <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter leading-tight">
          <E k="benefits_heading_line1" f="Four arenas." /><br /><E k="benefits_heading_line2" f="One deck." />
        </h2>
        <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
          <E k="benefits_intro" f="Every card you draw is a lens on the life you're already living. Here's where it sharpens the picture." />
        </p>
        <Link
          to="/book"
          data-testid="benefits-book-cta"
          className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
        >
          <CalendarCheck size={15} weight="fill" /> <E k="benefits_cta" f="Book a Session Now" /> <ArrowRight size={15} />
        </Link>
      </div>
      <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {benefits.map((b, i) => (
          <motion.div
            key={b.tag}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.1 }}
            className={`hover-lift bg-card border border-white/10 p-8 ${i % 2 === 1 ? "sm:translate-y-10" : ""}`}
            data-testid={`benefit-card-${i}`}
          >
            <b.icon size={34} weight="duotone" style={{ color: b.color }} />
            <p className="mt-5 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: b.color }}><E k={`benefit_${i}_tag`} f={b.tag} /></p>
            <h3 className="font-heading text-2xl font-semibold mt-2 tracking-tight"><E k={`benefit_${i}_title`} f={b.title} /></h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed"><E k={`benefit_${i}_text`} f={b.text} /></p>
            <div className="mt-6 pt-5 border-t border-white/10">
              <span className="font-heading text-3xl font-bold" style={{ color: b.color }}><E k={`benefit_${i}_stat`} f={b.stat} /></span>
              <p className="text-xs text-muted-foreground mt-1"><E k={`benefit_${i}_stat_label`} f={b.statLabel} /></p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
